import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { abrirDanfeNfePdf } from '../../utils/impressaoVenda';
import s from '../../pages/PedidosOrcamentos.module.css';

const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtQtd   = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 });
const fmtDataHora = str => str ? new Date(str).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const STATUS_BADGE = {
  rascunho:   { cls: s.badgeAberto,    label: 'Rascunho' },
  confirmada: { cls: s.badgeAprovado,  label: 'Confirmada' },
  transmitida:{ cls: s.badgeAprovado,  label: 'Transmitida' },
  cancelada:  { cls: s.badgeCancelado, label: 'Cancelada' },
};
const renderBadge = st => {
  const b = STATUS_BADGE[st] || STATUS_BADGE.rascunho;
  return <span className={b.cls}>{b.label}</span>;
};

/**
 * Consulta/navegação de uma devolução de compra já GERADA (confirmada ou
 * transmitida) — mesmo espírito de "olhar cabeçalho/itens/transporte antes
 * de enviar" que a tela de Entrada de NF já tem para as notas de compra.
 * Preview/Transmitir da NF-e ficam aqui (não no modal que só monta a
 * devolução — esse só escolhe quantidades/layout e gera).
 */
export default function DevolucaoCompraDetalhe({ devolucaoId, onClose, onAlterou }) {
  const [dev, setDev]           = useState(null);
  const [aba, setAba]           = useState('cabecalho');
  const [ocupado, setOcupado]   = useState(false);
  const [erro, setErro]         = useState('');

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const { data } = await api.get(`/devolucoes-compra/${devolucaoId}`);
      setDev(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar a devolução.');
    }
  }, [devolucaoId]);

  useEffect(() => { carregar(); }, [carregar]);

  const total = dev ? parseFloat(dev.total || 0) + parseFloat(dev.total_ipi || 0) : 0;

  const preview = () => abrirDanfeNfePdf(`/devolucoes-compra/${devolucaoId}/danfe-preview`);

  const transmitir = async () => {
    if (!window.confirm(
      `Transmitir a NF-e de devolução #${dev.id} ao fornecedor?\n\nTotal: ${fmtMoeda(total)}`
    )) return;
    setOcupado(true); setErro('');
    try {
      const { data } = await api.post(`/devolucoes-compra/${devolucaoId}/transmitir`);
      if (data.sucesso) {
        await carregar();
        onAlterou?.();
      } else {
        setErro(`NF-e rejeitada: ${data.motivo || data.status}`);
      }
    } catch (err) {
      const d = err.response?.data || {};
      setErro(d.erro || d.motivo || 'Erro ao transmitir NF-e.');
    } finally { setOcupado(false); }
  };

  const excluir = async () => {
    if (!window.confirm(
      dev.status === 'confirmada'
        ? 'Excluir esta devolução? O estoque baixado e o crédito do fornecedor serão revertidos.'
        : 'Excluir este rascunho de devolução?'
    )) return;
    setOcupado(true); setErro('');
    try {
      await api.delete(`/devolucoes-compra/${devolucaoId}`);
      onAlterou?.();
      onClose();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao excluir.');
      setOcupado(false);
    }
  };

  if (erro && !dev) {
    return (
      <div className={s.erpWrap}>
        <div className={s.titleBar}><span>📤</span> Devolução de Compra</div>
        <div className={s.tabContent}>
          <p style={{ color: '#b91c1c' }}>{erro}</p>
        </div>
        <div className={s.footer}>
          <div className={s.footerLeft} />
          <div className={s.footerRight}>
            <button className={s.btnErp} onClick={onClose}>Voltar</button>
          </div>
        </div>
      </div>
    );
  }
  if (!dev) return null;

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}>
        <span>📤</span> Devolução #{dev.id} — Entrada #{dev.entrada_codigo || dev.entrada_nf_id}
        {' '}{renderBadge(dev.status)}
      </div>

      <div className={s.tabBar}>
        {[
          { id: 'cabecalho',  label: 'Cabeçalho' },
          { id: 'itens',      label: `Itens (${dev.itens.length})` },
          { id: 'transporte', label: 'Transporte' },
        ].map(a => (
          <button key={a.id}
            className={`${s.tab} ${aba === a.id ? s.tabAtiva : ''}`}
            onClick={() => setAba(a.id)}>
            {a.label}
          </button>
        ))}
      </div>

      <div className={s.tabContent}>
        {erro && (
          <div style={{ background: '#fef2f2', border: '1px solid #ef4444', borderRadius: 4,
            padding: '6px 10px', fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>{erro}</div>
        )}

        {aba === 'cabecalho' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, maxWidth: 900 }}>
            <div className={s.campo}>
              <span className={s.campoLabel}>Entrada de origem</span>
              <input className={s.inputErp} readOnly
                value={`#${dev.entrada_codigo || dev.entrada_nf_id} — NF ${dev.entrada_numero_nf || 'S/N'}`} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Status</span>
              <div style={{ paddingTop: 6 }}>{renderBadge(dev.status)}</div>
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Layout (Tipo de Movimento)</span>
              <input className={s.inputErp} readOnly value={dev.layout_nome || dev.tipo_movimento_nome || '—'} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Criada em</span>
              <input className={s.inputErp} readOnly value={fmtDataHora(dev.criado_em)} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Confirmada em (gerada)</span>
              <input className={s.inputErp} readOnly value={fmtDataHora(dev.confirmada_em)} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Processa estoque / Gera financeiro</span>
              <input className={s.inputErp} readOnly
                value={`${dev.processa_estoque ? 'Sim' : 'Não'} / ${dev.gera_financeiro ? 'Sim' : 'Não'}`} />
            </div>
            {dev.status === 'transmitida' && (<>
              <div className={s.campo}>
                <span className={s.campoLabel}>NF-e nº</span>
                <input className={s.inputErp} readOnly value={dev.nfe_numero || '—'} />
              </div>
              <div className={s.campo} style={{ gridColumn: 'span 2' }}>
                <span className={s.campoLabel}>Chave de acesso</span>
                <input className={s.inputErp} readOnly style={{ fontFamily: 'monospace', fontSize: 11 }}
                  value={dev.nfe_chave || '—'} />
              </div>
            </>)}
            <div className={s.campo}>
              <span className={s.campoLabel}>Forma de pagamento</span>
              <input className={s.inputErp} readOnly value={dev.forma_pagamento || '—'} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Total (produtos + IPI)</span>
              <input className={s.inputErp} readOnly style={{ fontWeight: 700 }} value={fmtMoeda(total)} />
            </div>
          </div>
        )}

        {aba === 'itens' && (
          <div style={{ overflowX: 'auto' }}>
            <table className={s.itensGrid} style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>#</th>
                  <th style={{ width: 76 }}>Cód. Interno</th>
                  <th style={{ width: 90 }}>Cód. Forn.</th>
                  <th>Descrição</th>
                  <th style={{ width: 92 }}>NCM</th>
                  <th style={{ width: 52 }}>CFOP</th>
                  <th style={{ width: 42 }}>UN</th>
                  <th style={{ width: 80, textAlign: 'right' }}>Qtd</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Vl Unit</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Vl Total</th>
                </tr>
              </thead>
              <tbody>
                {dev.itens.map((it, idx) => (
                  <tr key={it.id}>
                    <td className={s.tdSeq}>{idx + 1}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, textAlign: 'center' }}>
                      {it.codigo_interno || '—'}
                    </td>
                    <td>{it.codigo_fornecedor || '—'}</td>
                    <td>{it.descricao}</td>
                    <td>{it.ncm || '—'}</td>
                    <td style={{ textAlign: 'center', fontFamily: 'monospace', fontWeight: 'bold', color: '#1d4ed8' }}
                      title={it.cfop_origem ? `Origem na entrada: ${it.cfop_origem}` : ''}>
                      {it.cfop_convertido || '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{it.unidade}</td>
                    <td style={{ textAlign: 'right' }}>{fmtQtd(it.quantidade)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoeda(it.valor_unitario)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoeda(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {aba === 'transporte' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, maxWidth: 900 }}>
            <div className={s.campo}>
              <span className={s.campoLabel}>Transportadora</span>
              <input className={s.inputErp} readOnly value={dev.transp_nome || '—'} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>CNPJ/CPF</span>
              <input className={s.inputErp} readOnly value={dev.transp_cnpj || '—'} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>UF</span>
              <input className={s.inputErp} readOnly value={dev.transp_uf || '—'} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Placa do veículo</span>
              <input className={s.inputErp} readOnly value={dev.veiculo_placa || '—'} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Qtd. volumes / Espécie</span>
              <input className={s.inputErp} readOnly value={`${dev.vol_quantidade || '—'} / ${dev.vol_especie || '—'}`} />
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Peso líquido / bruto</span>
              <input className={s.inputErp} readOnly value={`${dev.peso_liquido || '—'} / ${dev.peso_bruto || '—'}`} />
            </div>
          </div>
        )}
      </div>

      <div className={s.statusBar}>
        {dev.status === 'rascunho' && 'Rascunho — reabra pelo botão 📤 NF-e Devolução na entrada de origem para continuar.'}
        {dev.status === 'confirmada' && 'Confirmada — estoque baixado e crédito gerado. Falta emitir a NF-e.'}
        {dev.status === 'transmitida' && 'Transmitida — NF-e autorizada na SEFAZ.'}
      </div>

      <div className={s.footer}>
        <div className={s.footerLeft} />
        <div className={s.footerRight}>
          {dev.status !== 'rascunho' && (
            <button className={s.btnErp} disabled={ocupado} onClick={preview}
              title="Visualizar o DANFE para conferência">
              👁 Preview DANFE
            </button>
          )}
          {dev.status === 'confirmada' && (
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={ocupado} onClick={transmitir}>
              {ocupado ? 'Transmitindo…' : '📤 Transmitir NF-e'}
            </button>
          )}
          {dev.status === 'transmitida' && (
            <button className={s.btnErp}
              onClick={() => abrirDanfeNfePdf(`/fiscal/nfe/${dev.documento_fiscal_id}/danfe`)}>
              🖨️ Imprimir DANFE
            </button>
          )}
          {dev.status !== 'transmitida' && (
            <button className={`${s.btnErp} ${s.btnErpPerigo}`} disabled={ocupado} onClick={excluir}>
              Excluir
            </button>
          )}
          <div className={s.footerSep} />
          <button className={s.btnErp} disabled={ocupado} onClick={onClose}>Sair</button>
        </div>
      </div>
    </div>
  );
}
